// ─── Écran MOIS : pilote la vue prévu vs réalisé ──────────────────
// Charge les données, gère le mois affiché, le filtre par enveloppe, et les
// actions sur une occurrence (marquer absente / ignorée, rattacher un imprévu).
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, ScrollView, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import {
  PageHeader, useUI, useT, useAuth, useEspace, confirmerSuppression,
  usePileRetour,
  SelecteurDate,
} from '@mop/core';
import { ecouterBudgets, ecouterOperations, supprimerOperation, modifierOperation } from './services/budget';
import { ecouterConfigFinance } from './services/configFinance';
import { definirCategoriesPerso } from './data/categories';
import { estClos } from './data/statuts';
import { occurrencesDuMois, moisVoisinAvecPrevu } from './services/occurrences';
import {
  ecouterLignes, ecouterExceptions, creerLigne, modifierLigne, supprimerLigne,
  definirException, retirerException,
} from './services/lignes';
// `creerRattachement` et non `rattacher` : l'etat React `rattacher` (l'imprevu
// en cours de traitement) masquerait la fonction, et l'appel echouerait au
// runtime — invisible pour `expo export`.
import { ecouterRattachements, rattacher as creerRattachement, detacher, detacherTousDe } from './services/rattachements';
import { cleErreur } from './services/erreurs';
import VueMois from './VueMois';
import FeuilleLigne from './FeuilleLigne';
import FicheOccurrence from './FicheOccurrence';
// L'ancienne `FeuilleRattacher` (liste plate, une seule ligne à la fois) est
// remplacée le 16/08 par la MULTI-sélection en arbre — la même feuille que
// depuis les Comptes : deux portes, un seul écran.
import FeuilleRattacherLignes from './FeuilleRattacherLignes';
import FeuilleRattachement from './FeuilleRattachement';
import { proposerOperations } from './services/propositions';
import { ecouterComptes } from './services/comptes';
import { rattachementsAvecOperation, estSoldee } from './services/repartition';
import FeuilleFiltresMois from './FeuilleFiltresMois';
import BarreRecherche from './BarreRecherche';
import BarreMois from './BarreMois';
import SyntheseMois from './SyntheseMois';
import { prefSession, definirPrefSession } from './services/prefsSession';

const moisCourant = () => new Date().toISOString().slice(0, 7);

export default function EcranMois() {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const { tenantId, assurerEspace } = useEspace();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [mois, setMois] = useState(moisCourant);
  const [budgetIds, setBudgetIds] = useState([]);   // [] = toutes les enveloppes
  const [tri, setTri] = useState('statut');
  const [recherche, setRecherche] = useState('');
  const [filtres, setFiltres] = useState(null);     // onglet ouvert de la feuille
  const [budgets, setBudgets] = useState([]);
  const [actuals, setActuals] = useState([]);
  const [lignes, setLignes] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  // Les comptes servent au FILTRE par compte de la modale de rattachement.
  // Vraie liste, pas un tableau vide : le filtre serait muet sans le dire.
  const [comptes, setComptes] = useState([]);
  const [dateReq, setDateReq] = useState(null);   // selecteur de mois, modale soeur
  // Config finance de l'espace : categories perso, masquages, sous-cats.
  const [configCategories, setConfigCategories] = useState(null);
  // La charnière estimé ↔ réel. Fournie au moteur, elle FAIT FOI : le réel
  // d'une occurrence est la somme des PARTS qui lui sont affectées.
  const [rattachements, setRattachements] = useState([]);
  const [feuille, setFeuille] = useState(null);   // édition d'une ligne prévue
  const [fiche, setFiche] = useState(null);       // fiche INFO d'une occurrence
  const [rattacher, setRattacher] = useState(null); // imprévu → ligne prévue
  // La SYNTHÈSE se plie depuis l'en-tête (16/08) — mémorisé pour la session.
  const [syntheseVisible, setSyntheseVisible] = useState(() => prefSession('syntheseSuivi', true));
  const basculerSynthese = () => setSyntheseVisible(v => {
    definirPrefSession('syntheseSuivi', !v);
    return !v;
  });
  // Le sens INVERSE (12/08) : depuis une prévision, choisir l'opération du
  // compte qui l'honore. Même feuille que depuis une opération.
  const [occARattacher, setOccARattacher] = useState(null);
  // Une écoute qui échoue (règle, index, réseau) renvoie une liste VIDE : sans
  // remontée, l'écran affiche « rien ce mois-ci » et ment. On l'affiche.
  const [erreur, setErreur] = useState('');
  // D'où l'on vient, pour y revenir à la fermeture (Cédric, 13/08) :
  // ouvrir un écran depuis un autre oblige à FERMER le premier (deux
  // <Modal> imbriquées figent l'écran), sinon on retombe sur la liste.
  const retour = usePileRetour();
  const signaler = (quoi) => (e) => setErreur(`${quoi} — ${e?.message || e?.code || 'erreur'}`);

  useEffect(() => ecouterBudgets(tenantId, setBudgets, signaler('budgets')), [tenantId]);
  useEffect(() => ecouterConfigFinance(tenantId, (c) => {
    setConfigCategories(c);
    // Le registre d'affichage : les emojis des categories perso, partout.
    definirCategoriesPerso(c.categoriesPerso);
  }), [tenantId]);
  useEffect(() => ecouterOperations(tenantId, setActuals, signaler('operations')), [tenantId]);
  useEffect(() => ecouterLignes(tenantId, setLignes, signaler('lignesBudget')), [tenantId]);
  useEffect(() => ecouterExceptions(tenantId, setExceptions, signaler('occurrencesBudget')), [tenantId]);
  useEffect(() => ecouterComptes(tenantId, setComptes), [tenantId]);
  useEffect(() => ecouterRattachements(tenantId, setRattachements, signaler('rattachements')), [tenantId]);

  // Les motifs de rattachement deviennent des phrases : « rattachement.
  // montantNul » affiche tel quel est un code interne presente comme un
  // message (Cedric, 15/08). Un motif inconnu retombe sur le brut.
  const echec = (e) => {
    const cle = cleErreur(e);
    Alert.alert(t('commun.erreur'), cle ? t(cle) : (e?.message || ''));
  };

  const enregistrerLigne = async (l) => {
    try {
      if (feuille?.initial?.id) await modifierLigne(feuille.initial.id, l);
      else {
        const espaceId = tenantId || await assurerEspace();
        await creerLigne({ ...l, espaceId, creePar: user?.uid });
      }
      setFeuille(null);
      retour.revenir();
    } catch (e) { echec(e); }
  };

  // Gestes standard de l'app : swipe ← supprimer · swipe → éditer (+ ⇄ « autres
  // actions ») · tap = fiche INFO, d'où l'on passe en édition. Plus de menu
  // système : la fiche informe d'abord, elle ne modifie pas.
  const supprimerLignePrevue = (o) => confirmerSuppression(
    t('finances.actSupprimerLigne'), o.ligne?.libelle || '',
    () => supprimerLigne(o.ligneId, tenantId).catch(echec));

  const definirEtat = (o, statut) => definirException({
    espaceId: tenantId, ligneId: o.ligneId, mois, statut, creePar: user?.uid,
  }).catch(echec);

  // Bascule SOLDÉE depuis le swipe (Cédric, 17/08) : la MÊME action que le
  // bouton de `FicheOccurrence` (« rien de plus » ↔ « rouvrir »), un
  // raccourci en moins de gestes — plus besoin d'ouvrir la fiche pour ça.
  const toggleSoldee = (o) => (estClos(o) ? retirerException(o.ligneId, mois).catch(echec) : definirEtat(o, 'soldee'));

  // Mouvement INTERNE : l'opération sort des totaux sans être supprimée, et se
  // détache de sa ligne prévue (elle ne prouve plus rien). Réversible.
  const marquerInterne = (op, exclu) =>
    modifierOperation(op.id, { exclu, ...(exclu ? { budgetId: op.budgetId || null } : {}) }, op)
      // Une opération sortie des totaux ne prouve plus rien : ses parts sont
      // retirées, sinon elles gonfleraient encore le réel d'occurrences
      // qu'elle n'honore plus.
      .then(() => (exclu ? detacherTousDe({ espaceId: tenantId, depenseId: op.id }) : null))
      .catch(echec);

  // Une part par ligne cochée, en SÉQUENCE : chaque écriture doit voir les
  // précédentes — en parallèle, le contrôle du dépassement lirait une liste
  // de rattachements périmée. Même règle que côté Comptes.
  const rattacherParts = async (op, parts) => {
    try {
      const faits = [];
      for (const p of parts) {
        const id = await creerRattachement({
          depense: op, rattachements: [...rattachements, ...faits], espaceId: tenantId,
          ligneId: p.occurrence.ligneId, mois: p.occurrence.mois,
          montant: p.montant, origine: 'manuel', creePar: user?.uid,
        });
        faits.push({ id, depenseId: op.id,
          ligneId: p.occurrence.ligneId, mois: p.occurrence.mois, montant: p.montant });
      }
    } catch (e) { echec(e); }
  };

  // Détacher : on retire la PART, pas l'opération. Elle redevient (au moins en
  // partie) un imprévu, visible et re-rattachable.
  const detacherPart = (r) => detacher(r.id).catch(echec);

  // Depuis un imprévu : créer la ligne prévue déjà pré-remplie de l'opération.
  const creerDepuisImprevu = (op) => {
    setRattacher(null);
    setFeuille({ initial: {
      libelle: op.libelle || '', montant: op.montant, sens: op.sens,
      budgetId: op.budgetId || null, categorie: op.categorie || 'autre',
      debut: mois, frequence: 'mensuelle',
    } });
  };

  // Une occurrence n'est pas stockée, elle se RECALCULE : la fiche gardait le
  // cliché pris à l'ouverture et n'intégrait donc ni une édition ni un
  // rattachement fait depuis elle (Cédric, 13/08).
  const ficheVive = useMemo(() => {
    if (!fiche) return null;
    const l = lignes.find(x => x.id === fiche.ligneId);
    if (!l) return fiche;
    return occurrencesDuMois({
      lignes: [l], actuals, exceptions, rattachements, mk: fiche.mois,
    })[0] || fiche;
  }, [fiche, lignes, actuals, exceptions, rattachements]);

  return (
    <View style={{ flex: 1, backgroundColor: UI.bg }}>
      {/* « Mois courant » n'est PAS ici : il est collé au titre du mois, dans
          VueMois — même motif que la barre de My Calendar. */}
      <PageHeader titre={t('finances.vueMois')} iconeTitre="📊"
        action={{ icone: '＋', label: t('finances.nouvelleLigne'), onPress: () => setFeuille({ initial: null }) }}>
        {/* La bascule de SYNTHÈSE (16/08) : grisée quand le tableau est caché. */}
        <TouchableOpacity onPress={basculerSynthese} hitSlop={HIT}
          accessibilityLabel={t('finances.basculerSynthese')}>
          <Text style={{ fontSize: 19, marginLeft: 14, opacity: syntheseVisible ? 1 : 0.35 }}>📊</Text>
        </TouchableOpacity>
      </PageHeader>

      {/* EN-TÊTE FIXE (Cédric, 16/08) : la recherche et la navigation entre
          les mois restent sous la main pendant que la liste défile — même
          principe que le relevé de compte. */}
      <View style={s.enteteFixe}>
        {/* La synthèse AU-DESSUS de la recherche (Cédric, 16/08) : même ordre
            de lecture que Budget et Comptes — carte, recherche, mois. */}
        {syntheseVisible && (
          <SyntheseMois mois={mois} lignes={lignes} actuals={actuals}
            exceptions={exceptions} rattachements={rattachements} budgetIds={budgetIds} />
        )}
        <BarreRecherche valeur={recherche} onChange={setRecherche}
          nbFiltres={budgetIds.length} onFiltres={() => setFiltres('budgets')}
          triActif={tri !== 'statut'} onTri={() => setFiltres('tri')} />
        <BarreMois mois={mois} onMois={setMois}
          avant={moisVoisinAvecPrevu(lignes, mois, -1)}
          apres={moisVoisinAvecPrevu(lignes, mois, 1)} />
      </View>

      <ScrollView contentContainerStyle={s.corps}>
        {!!erreur && (
          <TouchableOpacity style={s.erreur} activeOpacity={0.8} onPress={() => setErreur('')}>
            <Text style={s.erreurTxt}>⚠️ {erreur}</Text>
          </TouchableOpacity>
        )}
        {/* Diagnostic : sans ligne prévue, l'écran est vide « normalement ».
            On distingue « aucune ligne » de « rien ce mois-ci ». */}
        {lignes.length === 0 && !erreur && (
          <Text style={s.info}>{t('finances.aucuneLigne')}</Text>
        )}
        <VueMois sansBarre
          mois={mois} lignes={lignes} actuals={actuals} exceptions={exceptions}
          rattachements={rattachements}
          budgets={budgets} budgetIds={budgetIds} recherche={recherche} tri={tri} onMois={setMois}
          onOccurrence={setFiche}
          onEditerLigne={(o) => setFeuille({ initial: o.ligne })}
          onSupprimerLigne={supprimerLignePrevue}
          onEtatOccurrence={toggleSoldee}
          onImprevu={setRattacher}
          onRattacher={setRattacher}
          onSupprimerImprevu={(op) => confirmerSuppression(
            t('commun.supprimer'), op.libelle || '',
            () => supprimerOperation(op.id, op).catch(echec))} />
      </ScrollView>

      <FicheOccurrence visible={!!fiche} occurrence={ficheVive} actuals={actuals} rattachements={rattachements}
        nomBudget={(budgets.find(b => b.id === fiche?.ligne?.budgetId) || {}).nom || ''}
        onFermer={() => setFiche(null)}
        onEditer={() => { const o = ficheVive; retour.empiler(() => setFiche(o)); setFeuille({ initial: o.ligne }); setFiche(null); }}
        onStatut={(st) => { definirEtat(fiche, st); setFiche(null); }}
        onRetablir={() => { retirerException(fiche.ligneId, mois).catch(echec); setFiche(null); }}
        onDetacher={detacherPart}
        onRattacherOperation={() => { const o = ficheVive; retour.empiler(() => setFiche(o)); setOccARattacher(o); setFiche(null); }} />

      {/* Depuis une PRÉVISION : choisir l'opération du compte qui l'honore.
          Les candidats sont proposés (libellé, montant, et surtout ce que
          l'utilisateur a déjà confirmé les mois précédents) — jamais appliqués. */}
      <FeuilleRattachement visible={!!occARattacher} source={occARattacher} depuisOccurrence
        /* Pour ELARGIR au-dela des propositions (Cedric, 13/08). */
        operations={actuals} rattachements={rattachements} comptes={comptes}
        candidats={occARattacher
          ? proposerOperations({ occurrence: occARattacher, operations: actuals, rattachements })
          : []}
        onValider={(c, montant) => {
          creerRattachement({
            depense: c.operation, rattachements, espaceId: tenantId,
            ligneId: occARattacher.ligneId, mois: occARattacher.mois,
            montant, origine: 'propose', creePar: user?.uid,
          }).catch(echec);
          setOccARattacher(null);
          retour.revenir();
        }}
        /* Ce qui est DÉJÀ rattaché à ce mois, et de quoi le défaire. */
        dejaRattaches={occARattacher
          ? rattachementsAvecOperation(rattachements, actuals, occARattacher.ligneId, occARattacher.mois)
          : []}
        onDetacher={(r) => detacher(r.id).catch(echec)}
        /* « Plus rien à ajouter » : une ANNOTATION du mois, pas un
           rattachement. Le moteur met alors le reste à dépenser à zéro. */
        soldee={!!occARattacher && estSoldee(exceptions, occARattacher.ligneId, occARattacher.mois)}
        onSolder={(v) => {
          const { ligneId, mois } = occARattacher;
          (v
            ? definirException({ espaceId: tenantId, ligneId, mois, statut: 'soldee', creePar: user?.uid })
            : retirerException(ligneId, mois)
          ).catch(echec);
          // On NE ferme PAS : le cadenas est une icone d'ETAT dans la carte,
          // on doit voir l'etat changer sur place plutot qu'etre ejecte.
        }}
        onFermer={() => { setOccARattacher(null); retour.revenir(); }} />

      <FeuilleRattacherLignes visible={!!rattacher} operation={rattacher} lignes={lignes}
        budgets={budgets} operations={actuals} exceptions={exceptions} rattachements={rattachements}
        onValider={(parts) => { rattacherParts(rattacher, parts); setRattacher(null); }}
        onCreerLigne={() => creerDepuisImprevu(rattacher)}
        onInterne={(op, exclu) => { marquerInterne(op, exclu); setRattacher(null); }}
        onFermer={() => setRattacher(null)} />

      <FeuilleLigne configCategories={configCategories} visible={!!feuille} initial={feuille?.initial} budgets={budgets}
        onDemanderMois={(valeur, onChoisir) => setDateReq({ valeur, onChoisir })}
        onEnregistrer={enregistrerLigne} onFermer={() => { setFeuille(null); retour.revenir(); }} />

      {/* ⚠️ APRES la feuille qui l'appelle, jamais avant. Ce sont deux <Modal>
          SOEURS — imbriquees, elles figeraient l'ecran — et entre soeurs c'est
          l'ORDRE DE RENDU qui decide laquelle passe devant. Rendu avant, le
          selecteur s'ouvrait DERRIERE la feuille : invisible, et l'ecran
          paraissait bloque (Cedric, 14/08). */}
      <SelecteurDate visible={!!dateReq} valeur={dateReq?.valeur} avecEffacer moisSeul
        onChange={(mk) => dateReq?.onChoisir(mk)} onFermer={() => setDateReq(null)} />

      <FeuilleFiltresMois visible={!!filtres} ongletInitial={filtres || 'budgets'}
        budgets={budgets} budgetIds={budgetIds} tri={tri}
        onAppliquer={({ budgetIds: b, tri: x }) => { setBudgetIds(b); setTri(x); }}
        onFermer={() => setFiltres(null)} />
    </View>
  );
}

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  // La limite entre le fixe et le défilant : sans bordure, la liste semble
  // glisser sous rien (même règle que le relevé de compte).
  enteteFixe: { paddingHorizontal: 12, paddingTop: 12, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  controles: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  // Carré : l'icône seule doit garder une cible tactile confortable (~42 px).
  btnIc: { width: 42, height: 42, borderRadius: 11, alignItems: 'center', justifyContent: 'center', borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card },
  btnIcOn: { borderColor: UI.accent, borderWidth: 1.5 },
  ic: { fontSize: 17 },
  pastille: { position: 'absolute', top: -4, right: -4, minWidth: 17, height: 17, borderRadius: 999, paddingHorizontal: 4, backgroundColor: UI.accent, alignItems: 'center', justifyContent: 'center' },
  pastilleTxt: { fontSize: 10.5, color: '#fff', fontWeight: '800' },
  rechercheZone: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12 },
  recherche: { flex: 1, paddingVertical: 10, fontSize: 14, color: UI.ink },
  erreur: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.danger || '#c0392b', padding: 11, marginBottom: 10 },
  erreurTxt: { fontSize: 13, color: UI.danger || '#c0392b', fontWeight: '700' },
  info: { fontSize: 13, color: UI.faint, lineHeight: 18, marginBottom: 10 },
});
